/**
 * useRemovalGuard.ts
 * Skill-gate hardening (#4) — warn before removing the last supervisor.
 *
 * Shared by the two "remove a whole user from a task" surfaces (the inline
 * picker chip-× and the Assign-Employees dialog chip-×). It runs the read-only
 * strand check and, when removing the user would leave juniors with no L3+
 * mentor, opens a warn-and-confirm whose confirm runs the caller's `proceed`.
 * Clean cases (or any check error) run `proceed` immediately — removal is never
 * hard-blocked.
 *
 * Logic-only: the consuming component renders
 *   <ConfirmDialog state={removalConfirm} onClose={closeRemovalConfirm} />
 */

import { useCallback, useRef, useState } from 'react';
import {
  CONFIRM_INITIAL,
  type ConfirmDialogState,
} from '../../../components/shared/ConfirmDialog';
import {
  checkRemovalStranding,
  type RemovalStrandResult,
} from '../../../services/scheduleTasks';

export interface UseRemovalGuardReturn {
  /**
   * Guard a removal: if removing `userKey` (shown as `displayName`) from
   * `taskKey` would strand juniors, open the confirm and run `proceed` only on
   * confirm; otherwise run `proceed` now. `scenarioKey` scopes the strand check
   * to the active draft (omit for the non-scenario task table).
   */
  guardRemoval: (
    taskKey: string,
    userKey: string,
    displayName: string,
    scenarioKey: string | undefined,
    proceed: () => void | Promise<void>,
  ) => Promise<void>;
  /** Confirm-dialog state to feed into <ConfirmDialog />. */
  removalConfirm: ConfirmDialogState;
  /** Close handler for <ConfirmDialog onClose={...} />. */
  closeRemovalConfirm: () => void;
}

export function useRemovalGuard(): UseRemovalGuardReturn {
  const [removalConfirm, setRemovalConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  // Synchronous re-entry latch. The async strand check used to leave a window
  // where the caller's own removingKey guard hadn't been set yet, so a rapid
  // second chip-× click could double-fire the removal. The latch is set on entry
  // (before any await) and released when the operation resolves or the confirm
  // is dismissed — closing that window for both call sites at once.
  const guardingRef = useRef(false);

  const closeRemovalConfirm = useCallback(() => {
    guardingRef.current = false; // dialog dismissed (cancel or confirm) → release
    setRemovalConfirm(CONFIRM_INITIAL);
  }, []);

  const guardRemoval = useCallback(
    async (
      taskKey: string,
      userKey: string,
      displayName: string,
      scenarioKey: string | undefined,
      proceed: () => void | Promise<void>,
    ): Promise<void> => {
      if (guardingRef.current) return; // a removal is already in flight for this picker
      guardingRef.current = true;

      let strand: RemovalStrandResult | null = null;
      try {
        strand = await checkRemovalStranding(taskKey, userKey, scenarioKey);
      } catch {
        strand = null; // check unavailable — never block the removal
      }

      if (strand?.strands && strand.strandedJuniors.length > 0) {
        const juniors = strand.strandedJuniors
          .map((j) => `${j.displayName} (L${j.level ?? '?'})`)
          .join(', ');
        // Latch stays held while the confirm is open; closeRemovalConfirm releases
        // it on cancel/confirm, and proceed runs guarded by the caller's removingKey.
        setRemovalConfirm({
          open: true,
          title: 'Remove the last supervisor?',
          message:
            `${displayName} is the only L3+ supervisor on this task. Removing them ` +
            `leaves ${juniors} with no supervisor. Remove anyway?`,
          confirmLabel: 'Remove anyway',
          confirmColor: 'warning',
          onConfirm: () => { void proceed(); },
        });
      } else {
        try {
          await proceed();
        } finally {
          guardingRef.current = false;
        }
      }
    },
    [],
  );

  return { guardRemoval, removalConfirm, closeRemovalConfirm };
}
